'use client';
import React, { useState } from 'react';
import LandingNavbar from '../components/LandingNavbar';
import LandingFooter from '../components/LandingFooter';

export default function ContactPage() {
  const [form, setForm] = useState({ name: '', email: '', subject: '', message: '' });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    // Simulate submission
    await new Promise((r) => setTimeout(r, 1200));
    setLoading(false);
    setSubmitted(true);
  };

  const channels = [
    {
      icon: '📧',
      title: 'Email Support',
      description: 'For general inquiries and seller support',
      value: 'suport@bozaat.com',
      href: 'mailto:suport@bozaat.com',
    },
    {
      icon: '⚖️',
      title: 'Legal & Compliance',
      description: 'Privacy, terms, and legal matters',
      value: 'suport@bozaat.com',
      href: 'mailto:suport@bozaat.com',
    },
    {
      icon: '🤝',
      title: 'Partnerships',
      description: 'Business partnerships and collaborations',
      value: 'suport@bozaat.com',
      href: 'mailto:suport@bozaat.com',
    },
    {
      icon: '💼',
      title: 'Seller Onboarding',
      description: 'Help with registration and KYC',
      value: 'suport@bozaat.com',
      href: 'mailto:suport@bozaat.com',
    },
  ];

  const faqs = [
    {
      q: 'How long does it take to get a response?',
      a: 'We typically respond within 24–48 business hours. Urgent seller issues are prioritized.',
    },
    {
      q: 'I\'m having trouble with my seller account. Who do I contact?',
      a: 'Use the support chat inside your seller dashboard for the fastest response, or email suport@bozaat.com.',
    },
    {
      q: 'How do I report a policy violation?',
      a: 'Email suport@bozaat.com with details of the violation. We investigate all reports seriously.',
    },
  ];

  return (
    <main className="min-h-screen bg-background">
      <LandingNavbar />

      {/* Header */}
      <section className="pt-28 pb-12 bg-gradient-to-br from-foreground via-foreground/95 to-primary/70 text-white">
        <div className="max-w-screen-xl mx-auto px-6 lg:px-10">
          <h1 className="text-4xl font-extrabold mb-3">Contact Us</h1>
          <p className="text-white/70 max-w-xl leading-relaxed">
            Have a question, feedback, or need help? We&apos;re here for you. Reach out through any of the channels below or send us a message directly.
          </p>
        </div>
      </section>

      <section className="py-14">
        <div className="max-w-screen-xl mx-auto px-6 lg:px-10">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-10">

            {/* Contact Form */}
            <div className="lg:col-span-3">
              <div className="bg-white rounded-2xl border border-border p-8">
                <h2 className="text-xl font-800 text-foreground mb-6">Send us a message</h2>

                {submitted ? (
                  <div className="text-center py-12">
                    <div className="text-5xl mb-4">✅</div>
                    <h3 className="text-lg font-700 text-foreground mb-2">Message Sent!</h3>
                    <p className="text-secondary-foreground text-sm">
                      Thank you for reaching out. We&apos;ll get back to you within 24–48 business hours.
                    </p>
                    <button
                      onClick={() => { setSubmitted(false); setForm({ name: '', email: '', subject: '', message: '' }); }}
                      className="mt-6 text-primary text-sm font-600 hover:underline"
                    >
                      Send another message
                    </button>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-5">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                      <div>
                        <label className="block text-sm font-600 text-foreground mb-1.5">Full Name *</label>
                        <input
                          type="text"
                          name="name"
                          value={form.name}
                          onChange={handleChange}
                          required
                          placeholder="Your full name"
                          className="w-full px-4 py-2.5 rounded-xl border border-border text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-all"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-600 text-foreground mb-1.5">Email Address *</label>
                        <input
                          type="email"
                          name="email"
                          value={form.email}
                          onChange={handleChange}
                          required
                          placeholder="you@example.com"
                          className="w-full px-4 py-2.5 rounded-xl border border-border text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-all"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-600 text-foreground mb-1.5">Subject *</label>
                      <select
                        name="subject"
                        value={form.subject}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-2.5 rounded-xl border border-border text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-all bg-white"
                      >
                        <option value="">Select a subject</option>
                        <option value="seller-support">Seller Support</option>
                        <option value="account-issue">Account Issue</option>
                        <option value="payout-query">Payout Query</option>
                        <option value="kyc-help">KYC Help</option>
                        <option value="partnership">Partnership Inquiry</option>
                        <option value="legal">Legal / Compliance</option>
                        <option value="other">Other</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-600 text-foreground mb-1.5">Message *</label>
                      <textarea
                        name="message"
                        value={form.message}
                        onChange={handleChange}
                        required
                        rows={5}
                        placeholder="Describe your query or issue in detail..."
                        className="w-full px-4 py-2.5 rounded-xl border border-border text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-all resize-none"
                      />
                    </div>
                    <button
                      type="submit"
                      disabled={loading}
                      className="btn-primary w-full py-3 rounded-xl font-700 text-sm disabled:opacity-60 disabled:cursor-not-allowed"
                    >
                      {loading ? 'Sending...' : 'Send Message'}
                    </button>
                  </form>
                )}
              </div>
            </div>

            {/* Right Column */}
            <div className="lg:col-span-2 space-y-6">
              {/* Contact Channels */}
              <div className="bg-white rounded-2xl border border-border p-6">
                <h2 className="text-base font-800 text-foreground mb-5">Other ways to reach us</h2>
                <div className="space-y-4">
                  {channels.map((c, i) => (
                    <a
                      key={i}
                      href={c.href}
                      className="flex gap-4 p-3 rounded-xl hover:bg-muted/50 transition-colors group"
                    >
                      <div className="flex-shrink-0 text-2xl">{c.icon}</div>
                      <div>
                        <p className="text-sm font-700 text-foreground group-hover:text-primary transition-colors">{c.title}</p>
                        <p className="text-xs text-secondary-foreground mb-0.5">{c.description}</p>
                        <p className="text-xs text-primary font-500">{c.value}</p>
                      </div>
                    </a>
                  ))}
                </div>
              </div>

              {/* Response Time */}
              <div className="bg-primary/5 rounded-2xl border border-primary/10 p-6">
                <h3 className="text-sm font-700 text-foreground mb-3">⏱ Response Times</h3>
                <div className="space-y-2">
                  {[
                    { type: 'Seller Dashboard Chat', time: 'Within 2 hours' },
                    { type: 'Email Support', time: '24–48 hours' },
                    { type: 'Legal Queries', time: '3–5 business days' },
                  ].map((r, i) => (
                    <div key={i} className="flex justify-between text-xs">
                      <span className="text-secondary-foreground">{r.type}</span>
                      <span className="font-600 text-foreground">{r.time}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Company Address */}
              <div className="bg-white rounded-2xl border border-border p-6">
                <h3 className="text-sm font-700 text-foreground mb-3">📍 Our Address</h3>
                <div className="space-y-1 text-xs text-secondary-foreground leading-relaxed">
                  <p className="font-700 text-foreground text-sm">Bozaat</p>
                  <p>Flat No. 202, Sai Residency,</p>
                  <p>Opp. City Mall, MG Road,</p>
                  <p>Pune, Maharashtra – 411001</p>
                  <p>India</p>
                </div>
              </div>

              {/* FAQs */}
              <div className="bg-white rounded-2xl border border-border p-6">
                <h3 className="text-sm font-700 text-foreground mb-4">Common Questions</h3>
                <div className="space-y-4">
                  {faqs.map((faq, i) => (
                    <div key={i}>
                      <p className="text-xs font-700 text-foreground mb-1">{faq.q}</p>
                      <p className="text-xs text-secondary-foreground leading-relaxed">{faq.a}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <LandingFooter />
    </main>
  );
}
